<script setup>
import { h, ref, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Editor from 'next/Editor/Editor.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const labelToApply = ref({});
const excludedLabelConditions = ref([]);
const ignoreWaiting = ref(false);
const isEnabled = ref(false);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

const labels = useMapGetter('labels/getLabels');

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const statusOptions = computed(() => [
  {
    id: 'open',
    name: t(
      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.STATUSES.open'
    ),
  },
  {
    id: 'pending',
    name: t(
      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.STATUSES.pending'
    ),
  },
  {
    id: 'snoozed',
    name: t(
      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.STATUSES.snoozed'
    ),
  },
]);

const selectedLabelName = computed(() => {
  return labelToApply.value?.name ?? null;
});

const buildConditionFromSaved = condition => ({
  status: statusOptions.value.find(s => s.id === condition.status) ?? null,
  labels: labelOptions.value.filter(l =>
    (condition.labels || []).includes(l.name)
  ),
});

watch(
  [currentAccount, labelOptions],
  () => {
    const {
      auto_resolve_after,
      auto_resolve_message,
      auto_resolve_ignore_waiting,
      auto_resolve_label,
      auto_resolve_excluded_label_conditions,
    } = currentAccount.value?.settings || {};

    duration.value = auto_resolve_after;
    message.value = auto_resolve_message;
    ignoreWaiting.value = auto_resolve_ignore_waiting;
    labelToApply.value = labelOptions.value.find(
      option => option.name === auto_resolve_label
    );
    excludedLabelConditions.value = (
      auto_resolve_excluded_label_conditions || []
    ).map(buildConditionFromSaved);

    if (duration.value) {
      if (duration.value % (24 * 60) === 0) {
        unit.value = DURATION_UNITS.DAYS;
      } else if (duration.value % 60 === 0) {
        unit.value = DURATION_UNITS.HOURS;
      } else {
        unit.value = DURATION_UNITS.MINUTES;
      }
    }

    if (duration.value) {
      isEnabled.value = true;
    }
  },
  { deep: true, immediate: true }
);

const addCondition = () => {
  excludedLabelConditions.value.push({ status: null, labels: [] });
};

const removeCondition = index => {
  excludedLabelConditions.value.splice(index, 1);
};

const serializeConditions = () =>
  excludedLabelConditions.value
    .filter(c => c.status && c.labels.length > 0)
    .map(c => ({ status: c.status.id, labels: c.labels.map(l => l.name) }));

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (duration.value < 10) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
    return Promise.resolve();
  }

  return updateAccountSettings({
    auto_resolve_after: duration.value,
    auto_resolve_message: message.value,
    auto_resolve_ignore_waiting: ignoreWaiting.value,
    auto_resolve_label: selectedLabelName.value,
    auto_resolve_excluded_label_conditions: serializeConditions(),
  });
};

const handleDisable = async () => {
  duration.value = null;
  message.value = '';

  return updateAccountSettings({
    auto_resolve_after: null,
    auto_resolve_message: '',
    auto_resolve_ignore_waiting: false,
    auto_resolve_label: null,
    auto_resolve_excluded_labels: [],
    auto_resolve_excluded_label_conditions: [],
  });
};

const toggleAutoResolve = async () => {
  if (!isEnabled.value) handleDisable();
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleAutoResolve" />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP')"
        >
          <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
            <!-- allow 10 mins to 999 days -->
            <DurationInput
              v-model="duration"
              v-model:unit="unit"
              min="0"
              max="1438560"
              class="w-full"
            />
          </div>
        </WithLabel>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL')"
          :help-message="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP')"
        >
          <Editor
            v-model="message"
            class="w-full"
            channel-type="Context::NoToolbar"
            enable-variables
            :enable-canned-responses="false"
            :show-character-count="false"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
            "
          />
        </WithLabel>
        <WithLabel :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.PREFERENCES')">
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
          >
            <div class="p-3 h-12 flex items-center justify-between">
              <span>
                {{
                  t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL')
                }}
              </span>
              <Switch v-model="ignoreWaiting" />
            </div>
            <div class="p-3 h-12 flex items-center justify-between">
              <span>
                {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
              </span>
              <SingleSelect
                v-model="labelToApply"
                :options="labelOptions"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
                "
                placeholder-icon="i-lucide-chevron-down"
                placeholder-trailing-icon
                variant="faded"
              />
            </div>
            <div class="p-3 flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <div class="flex flex-col gap-1">
                  <span>
                    {{
                      t(
                        'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{
                      t(
                        'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.HELP'
                      )
                    }}
                  </span>
                </div>
                <NextButton
                  type="button"
                  ghost
                  slate
                  size="small"
                  :label="
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.ADD_CONDITION'
                    )
                  "
                  icon="i-lucide-plus"
                  @click="addCondition"
                />
              </div>
              <div
                v-for="(condition, index) in excludedLabelConditions"
                :key="index"
                class="flex items-center gap-2"
              >
                <SingleSelect
                  v-model="condition.status"
                  :options="statusOptions"
                  :placeholder="
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.STATUS_PLACEHOLDER'
                    )
                  "
                  placeholder-icon="i-lucide-chevron-down"
                  placeholder-trailing-icon
                  variant="faded"
                  class="min-w-[120px]"
                />
                <MultiSelect
                  v-model="condition.labels"
                  :options="labelOptions"
                  class="flex-1"
                />
                <button
                  type="button"
                  class="text-xs text-n-ruby-9 hover:text-n-ruby-11 shrink-0"
                  @click="removeCondition(index)"
                >
                  {{
                    t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.EXCLUDED_LABEL_CONDITIONS.REMOVE_CONDITION'
                    )
                  }}
                </button>
              </div>
            </div>
          </div>
        </WithLabel>
        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.UPDATE_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
